import {
  Box,
  ControlPrefix,
  FormControlSizes,
  IconName,
  MultiSelectCheckboxDropdown,
  useCurrenciesContext,
  type FilterableSelectProperty,
} from '@talos/kyoko';
import { useMemo } from 'react';

interface AggregatedAssetSelectProps {
  /** All Talos symbols available for the active metric — the checklist options. */
  availableTalosSymbols: string[];
  /** Currently selected assets as Talos symbols (e.g. `['BTC','ETH']`). */
  selectedTalosSymbols: string[];
  /** Fires live on every check/uncheck — the parent persists immediately. */
  onSelectedTalosSymbolsChange: (talosSymbols: string[]) => void;
  buttonTestID: string;
  dropdownTestID: string;
  /** Fixed width (e.g. the toolbar). Omit to fill the container (e.g. the settings panel). */
  width?: string;
  /** When set, unselected options are disabled once this many assets are selected. */
  maxSelections?: number;
  /** Render the dropdown in a top-level portal so it escapes ancestor `overflow` clipping (e.g. a config panel). */
  portalize?: boolean;
}

export function AggregatedAssetSelect({
  availableTalosSymbols,
  selectedTalosSymbols,
  onSelectedTalosSymbolsChange,
  buttonTestID,
  dropdownTestID,
  width,
  maxSelections,
  portalize,
}: AggregatedAssetSelectProps) {
  const { currenciesBySymbol } = useCurrenciesContext();

  // allowSelectAll is off — a one-click select-all over all assets would blow the fetch budget.
  const assetProperty = useMemo<FilterableSelectProperty<string, string>>(
    () => ({
      key: 'asset',
      label: 'Assets',
      icon: IconName.QuestionMarkCircle,
      options: availableTalosSymbols,
      getOptionLabel: symbol => symbol,
      getOptionDescription: symbol => currenciesBySymbol.get(symbol)?.Description ?? '',
      allowSelectAll: false,
    }),
    [availableTalosSymbols, currenciesBySymbol]
  );

  const isItemDisabled = useMemo(
    () =>
      maxSelections != null && selectedTalosSymbols.length >= maxSelections
        ? (symbol: string) => !selectedTalosSymbols.includes(symbol)
        : undefined,
    [maxSelections, selectedTalosSymbols]
  );

  const dropdown = (
    <MultiSelectCheckboxDropdown
      prefix={<ControlPrefix>Assets</ControlPrefix>}
      property={assetProperty}
      selections={selectedTalosSymbols}
      onSelectionsChange={onSelectedTalosSymbolsChange}
      size={FormControlSizes.Small}
      buttonTestID={buttonTestID}
      dropdownTestID={dropdownTestID}
      isItemDisabled={isItemDisabled}
      portalize={portalize}
    />
  );

  // Fixed-width wrapper controls the "+N" overflow point; omit to fill the parent (settings panel).
  return width != null ? (
    <Box w={width} flexShrink={0}>
      {dropdown}
    </Box>
  ) : (
    dropdown
  );
}
